import React, { useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, Button, useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Coffee, CheckCircle, XCircle } from 'lucide-react-native';
import {
  useVerifyEmail,
  useResendEmailVerification,
} from '@coffeeplexity/api-client';
import Toast from 'react-native-toast-message';

export function VerifyEmailScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const verifyEmailMutation = useVerifyEmail();
  const resendVerificationMutation = useResendEmailVerification();

  const [verificationStatus, setVerificationStatus] = useState<
    'loading' | 'success' | 'error'
  >('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = route.params?.token;
    const emailParam = route.params?.email;

    if (emailParam) {
      setEmail(emailParam);
    }

    if (token) {
      verifyEmail(token);
    } else {
      setVerificationStatus('error');
      setErrorMessage('Invalid verification link');
    }
  }, [route.params]);

  const verifyEmail = async (token: string) => {
    try {
      await verifyEmailMutation.mutateAsync(token);
      setVerificationStatus('success');
      Toast.show({
        type: 'success',
        text1: 'Email Verified!',
        text2: 'Your email has been successfully verified.',
      });
    } catch (error: any) {
      setVerificationStatus('error');
      if (error.status === 400) {
        setErrorMessage('Invalid or expired verification token');
      } else if (error.status === 409) {
        setErrorMessage('Email is already verified');
      } else {
        setErrorMessage('An error occurred during verification');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setErrorMessage('Email address is required to resend verification');
      return;
    }

    try {
      await resendVerificationMutation.mutateAsync(email);
      setErrorMessage('');
      Toast.show({
        type: 'success',
        text1: 'Verification Email Sent',
        text2: 'Please check your email for the verification link.',
      });
    } catch (error: any) {
      setErrorMessage('Failed to resend verification email');
      Toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Failed to resend verification email',
      });
    }
  };

  if (verificationStatus === 'loading') {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.content}>
          <Coffee size={64} color={theme.colors.primary} />
          <Text
            variant='headlineMedium'
            style={[styles.title, { color: theme.colors.onBackground }]}
          >
            Verifying your email
          </Text>
          <Text
            variant='bodyLarge'
            style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
          >
            Please wait while we verify your email address...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.content}>
          <CheckCircle size={64} color={theme.colors.primary} />
          <Text
            variant='headlineMedium'
            style={[styles.title, { color: theme.colors.onBackground }]}
          >
            Email verified!
          </Text>
          <Text
            variant='bodyLarge'
            style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
          >
            Your email address has been successfully verified. You can now log
            in to your account.
          </Text>
          <Button
            mode='contained'
            onPress={() => navigation.navigate('login' as never)}
            style={[styles.button, { backgroundColor: theme.colors.primary }]}
            contentStyle={styles.buttonContent}
          >
            Go to Login
          </Button>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <View style={styles.content}>
        <XCircle size={64} color={theme.colors.error} />
        <Text
          variant='headlineMedium'
          style={[styles.title, { color: theme.colors.onBackground }]}
        >
          Verification failed
        </Text>
        <Text
          variant='bodyLarge'
          style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
        >
          {errorMessage || "We couldn't verify your email address."}
        </Text>

        <View style={styles.buttonContainer}>
          {email && (
            <Button
              mode='contained'
              onPress={handleResendVerification}
              loading={resendVerificationMutation.isPending}
              disabled={resendVerificationMutation.isPending}
              style={[styles.button, { backgroundColor: theme.colors.primary }]}
              contentStyle={styles.buttonContent}
            >
              Resend verification email
            </Button>
          )}

          <Button
            mode='text'
            onPress={() => navigation.navigate('login' as never)}
            style={styles.textButton}
          >
            Back to Login
          </Button>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  title: {
    marginTop: 24,
    marginBottom: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: 32,
  },
  buttonContainer: {
    width: '100%',
    gap: 16,
  },
  button: {
    width: '100%',
  },
  buttonContent: {
    height: 48,
  },
  textButton: {
    alignSelf: 'center',
  },
});
